<template>
  <div class="download-session">
    <div class="status-item d-flex justify-content-between align-items-center">
      <div class="session-progress flex-grow-1">
        <div class="name d-flex align-items-center">
          <CIcon size="custom-size" :height="14" name="cipDownload" class="mr-1" />
          {{ session.name }}
        </div>
        <!-- Show indeterminate progress while preparing (single download or batch with 0 downloaded) -->
        <div v-if="isInProgress && isPreparingDownload" class="download-progress__track small my-2 indeterminate">
          <div class="download-progress__bar"></div>
        </div>
        <!-- Batch download with progress: show actual progress bar -->
        <DownloadProgress
            v-else-if="!isFailed && !isSingleDownload && !isPreparingDownload"
            :total-files="session.totalFiles"
            :downloaded-files="session.downloadedFiles"
        />
        <div class="info">
          <template v-if="isPending">
            <span v-translate translate-context="downloader.session">Preparing download...</span>
          </template>
          <template v-else-if="isInProgress">
            <!-- Single download: just show file count -->
            <template v-if="isSingleDownload">
              <translate translate-context="downloader.session"
                         :translate-params="{count: session.totalFiles}"
                         :translate-n="session.totalFiles"
                         translate-plural="%{count} files">
                %{count} file
              </translate>
            </template>
            <!-- Batch download: show file count + zip progress -->
            <template v-else>
              <translate translate-context="downloader.session"
                         :translate-params="{count: session.totalFiles}"
                         :translate-n="session.totalFiles"
                         translate-plural="%{count} files">
                %{count} file
              </translate>
              -
              <translate translate-context="downloader.session"
                         :translate-params="{current: session.currentBatch, total: session.totalBatches}">
                zip %{current}/%{total}
              </translate>
            </template>
          </template>
          <template v-else-if="isCompleted">
            <translate translate-context="downloader.session"
                       :translate-params="{count: session.totalFiles}"
                       :translate-n="session.totalFiles"
                       translate-plural="%{count} files downloaded">
              %{count} file downloaded
            </translate>
          </template>
          <template v-else-if="isCancelled">
            <span class="text-warning" v-translate translate-context="downloader.session">Download cancelled</span>
          </template>
          <template v-else-if="isFailed">
            <span class="text-danger" v-translate translate-context="downloader.session">Download failed, please try again</span>
          </template>
        </div>
      </div>
      <div class="ml-2">
        <CButtonClose
            v-if="isInProgress"
            @click="onCancel"
            class="list-item-closer"
            v-c-tooltip="$gettext('Stop download')"
        >
          <CIcon name="cipClose" size="custom-size" width="15"/>
        </CButtonClose>
        <CButtonClose
            v-else
            @click="onClose"
            class="list-item-closer"
        >
          <CIcon name="cipClose" size="custom-size" width="15"/>
        </CButtonClose>
      </div>
    </div>
  </div>
</template>

<script>
import DownloadProgress from './DownloadProgress.vue'
import downloadSessionStatus from '../downloadSessionStatus'

export default {
  name: 'DownloadSession',
  components: { DownloadProgress },
  emits: ['cancel', 'close'],
  props: {
    session: {
      type: Object,
      required: true
    }
  },
  computed: {
    isPending() {
      return this.session.status === downloadSessionStatus.pending
    },
    isInProgress() {
      return this.session.status === downloadSessionStatus.inProgress
    },
    isCompleted() {
      return this.session.status === downloadSessionStatus.completed
    },
    isCancelled() {
      return this.session.status === downloadSessionStatus.cancelled
    },
    isFailed() {
      return this.session.status === downloadSessionStatus.failed
    },
    isSingleDownload() {
      return this.session.totalBatches <= 1
    },
    isPreparingDownload() {
      // Show indeterminate state while server is preparing (no files downloaded yet)
      return this.isSingleDownload || this.session.downloadedFiles === 0
    }
  },
  methods: {
    onCancel() {
      this.$emit('cancel', this.session.id)
    },
    onClose() {
      this.$emit('close', this.session.id)
    }
  }
}
</script>

<style scoped>
.download-session {
  padding: 8px 0;
  border-bottom: 1px solid #ebedef;
}

.download-session:last-child {
  border-bottom: none;
}

.name {
  font-weight: 500;
  font-size: 13px;
  color: #3c4b64;
}

.info {
  font-size: 12px;
  color: #768192;
  margin-top: 2px;
}

.list-item-closer {
  opacity: 0.6;
}

.list-item-closer:hover {
  opacity: 1;
}

/* Indeterminate progress bar for single downloads */
.download-progress__track {
  height: 6px;
  background-color: #e9ecef;
  border-radius: 3px;
  overflow: hidden;
}

.download-progress__track.indeterminate .download-progress__bar {
  width: 30%;
  height: 100%;
  background-color: #2eb85c;
  border-radius: 3px;
  animation: indeterminate 1.5s ease-in-out infinite;
}

@keyframes indeterminate {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(400%);
  }
}
</style>
